/**
 * ScrollFade
 *
 * Toggles edge-fade state on horizontally scrollable containers marked with
 * `[data-scroll-fade]`, so a start/end gradient is only shown when there is more
 * content to scroll towards on that side:
 *   - `data-fade-start` present  → content is hidden off the left edge
 *   - `data-fade-end` present    → content is hidden off the right edge
 *
 * The gradient itself is pure CSS (a mask driven by these attributes); this only
 * sets the attributes. It survives SPA navigation and Sprig swaps via a small set
 * of lifecycle triggers + delegated scroll handling (no per-element binding, so
 * re-rendered DOM is picked up automatically).
 */

const SELECTOR = '[data-scroll-fade]'
const EPSILON = 1

function update(el) {
  if (!el || el.nodeType !== 1) return

  const max = el.scrollWidth - el.clientWidth
  const canScroll = max > EPSILON
  const x = el.scrollLeft

  el.toggleAttribute('data-fade-start', canScroll && x > EPSILON)
  el.toggleAttribute('data-fade-end', canScroll && x < max - EPSILON)
}

function updateAll() {
  document.querySelectorAll(SELECTOR).forEach(update)
}

export const ScrollFade = {
  _inited: false,
  _scheduled: false,

  init() {
    if (this._inited) return
    this._inited = true

    // Scroll events don't bubble — listen in the capture phase and match the target.
    document.addEventListener(
      'scroll',
      (e) => {
        const el = e.target
        if (el && el.nodeType === 1 && el.matches && el.matches(SELECTOR)) update(el)
      },
      { capture: true, passive: true }
    )

    window.addEventListener('resize', () => this.schedule(), { passive: true })
    window.addEventListener('load', () => this.schedule())

    // Re-evaluate after Sprig / htmx swaps replace the container's content.
    document.addEventListener('htmx:afterSettle', () => this.schedule())

    this.schedule()
  },

  // Coalesce bursts of triggers into a single measurement per frame.
  schedule() {
    if (this._scheduled) return
    this._scheduled = true
    requestAnimationFrame(() => {
      this._scheduled = false
      updateAll()
    })
  },
}
